import React from 'react';
import { Menu, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from '@tanstack/react-router';
import Logo from '@/components/ui/logo';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import { useAppShell } from './useAppShell';
import { studioBOn } from '@/lib/studioB/flag';

interface AppHeaderProps {
  /** Breadcrumb trail, e.g. [{ label: 'Captures' }, { label: 'My Capture' }] */
  breadcrumbs?: Array<{ label: string; to?: string }>;
  /** Slot for page-specific actions on the right side of the header */
  actions?: React.ReactNode;
  /** Hide the breadcrumb block on small screens */
  className?: string;
}

/**
 * Shared app-level header used on Videos, Captures, and other standalone list
 * pages. Left side: hamburger (phones, inside the AppShell) → nav drawer, logo
 * (home; left out while the open rail shows it), breadcrumb.
 * Right side: theme toggle + optional page actions.
 *
 * Matches the `studio-topbar` aesthetic used by the Hero screen so navigation
 * across pages feels continuous.
 */
export function AppHeader({ breadcrumbs = [], actions, className }: AppHeaderProps) {
  const shell = useAppShell();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <header className={cn('sticky top-0 z-30 studio-topbar', className)}>
      <div className="w-full px-4 h-14 flex items-center gap-3">
        {shell?.isPhone && (
          <Button
            variant="ghost"
            size="sm"
            onClick={shell.openNav}
            className="h-9 w-9 p-0 hover:bg-black/5 dark:hover:bg-white/5"
            title="Open menu"
          >
            <Menu className="h-5 w-5 studio-text-secondary" />
          </Button>
        )}

        {/* Logo → home */}
        {(shell ? shell.logoInBar : true) && (
          <Link
            to="/"
            search={{ slide: undefined }}
            className="flex items-center rounded-lg -mx-1 px-1 hover:bg-black/5 dark:hover:bg-white/5 transition"
            title="Go to home"
          >
            <Logo variant="modern" className="h-8" />
          </Link>
        )}

        {/* Breadcrumb trail */}
        {breadcrumbs.length > 0 && (
          <nav className="hidden md:flex items-center gap-2 text-sm ml-2">
            <span className="text-muted-foreground/40">/</span>
            {breadcrumbs.map((b, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={i}>
                  {b.to && !isLast ? (
                    <button
                      onClick={() => navigate({ to: b.to! })}
                      className="studio-text-secondary hover:studio-text-primary transition"
                    >
                      {b.label}
                    </button>
                  ) : (
                    <span className={isLast ? 'studio-text-primary font-medium' : 'studio-text-secondary'}>
                      {b.label}
                    </span>
                  )}
                  {!isLast && <span className="text-muted-foreground/40">/</span>}
                </React.Fragment>
              );
            })}
          </nav>
        )}

        {/* Right side: actions + theme toggle (Studio B keeps day/night in the rail's account menu) */}
        <div className="ml-auto flex items-center gap-1">
          {actions}
          {!studioBOn() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="h-9 w-9 p-0 hover:bg-black/5 dark:hover:bg-white/5"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 studio-text-secondary" />
              ) : (
                <Moon className="h-5 w-5 studio-text-secondary" />
              )}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
